/**
 * Admin Password Service
 * Handles password reset for admin accounts using the existing admin system
 */

import { ADMIN_CONFIG } from '../config/admin';

// In a real application, this would be stored securely in a database
// For demo purposes, we'll use localStorage with encryption
const ADMIN_STORAGE_KEY = 'admin_password_reset';
const ADMIN_CREDENTIALS_KEY = 'admin_credentials';

class AdminPasswordService {
  constructor() {
    // Load admin credentials from configuration
    this.adminCredentials = {
      username: ADMIN_CONFIG.credentials.username,
      password: 'admin123', // This will be updated when password is reset
      email: ADMIN_CONFIG.credentials.email
    };
    
    // Load existing credentials from storage
    this.loadCredentials();
  }

  loadCredentials() {
    try {
      const stored = localStorage.getItem(ADMIN_CREDENTIALS_KEY);
      if (stored) {
        this.adminCredentials = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading admin credentials:', error);
    }
  }

  saveCredentials() {
    try {
      localStorage.setItem(ADMIN_CREDENTIALS_KEY, JSON.stringify(this.adminCredentials));
    } catch (error) {
      console.error('Error saving admin credentials:', error);
    }
  }

  /**
   * Generate a secure reset token
   */
  generateResetToken() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2);
    return btoa(`${timestamp}-${random}`).replace(/[^a-zA-Z0-9]/g, '');
  }

  /**
   * Send password reset email (simulated)
   */
  async sendPasswordResetEmail(email) {
    // Validate email matches admin email
    if (email !== this.adminCredentials.email) {
      throw new Error('Email address not found in admin records');
    }

    // Generate reset token
    const resetToken = this.generateResetToken();
    const expiresAt = Date.now() + ADMIN_CONFIG.security.passwordResetTokenExpiry;

    // Store reset token
    const resetData = {
      token: resetToken,
      email: email,
      expiresAt: expiresAt,
      used: false
    };

    localStorage.setItem(ADMIN_STORAGE_KEY, JSON.stringify(resetData));

    // In a real application, this would send an actual email
    // For demo purposes, we'll simulate the email sending
    if (import.meta.env.DEV) {
      console.log('Password reset email sent to:', email);
      console.log('Reset token:', resetToken);
      console.log('Reset link:', `${window.location.origin}/admin/reset-password?token=${resetToken}`);
    }
    
    // Show the reset link in an alert for demo purposes (only in development)
    if (ADMIN_CONFIG.development.showResetLinksInConsole) {
      alert(`DEMO: Password reset link generated!\n\nReset Link: ${window.location.origin}/admin/reset-password?token=${resetToken}\n\nIn production, this would be sent via email.`);
    }

    // Simulate email sending delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    return {
      success: true,
      message: 'Password reset instructions have been sent to your email address.'
    };
  }

  /**
   * Validate reset token
   */
  validateResetToken(token) {
    try {
      const stored = localStorage.getItem(ADMIN_STORAGE_KEY);
      if (!stored) {
        return { valid: false, error: 'No reset token found' };
      }

      const resetData = JSON.parse(stored);
      
      if (resetData.token !== token) {
        return { valid: false, error: 'Invalid reset token' };
      }

      if (resetData.used) {
        return { valid: false, error: 'Reset token has already been used' };
      }

      if (Date.now() > resetData.expiresAt) {
        return { valid: false, error: 'Reset token has expired' };
      }

      return { valid: true, email: resetData.email };
    } catch (error) {
      return { valid: false, error: 'Invalid reset token format' };
    }
  }

  /**
   * Reset admin password
   */
  async resetPassword(token, newPassword) {
    // Validate token
    const validation = this.validateResetToken(token);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Validate new password
    const passwordValidation = this.validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      throw new Error(`Password requirements: ${passwordValidation.errors.join(', ')}`);
    }

    // Update admin credentials
    this.adminCredentials.password = newPassword;
    this.saveCredentials();

    // Mark token as used
    const resetData = JSON.parse(localStorage.getItem(ADMIN_STORAGE_KEY));
    resetData.used = true;
    localStorage.setItem(ADMIN_STORAGE_KEY, JSON.stringify(resetData));

    return {
      success: true,
      message: 'Password has been reset successfully'
    };
  }

  /**
   * Validate password strength
   */
  validatePassword(password) {
    const minLength = 12;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      isValid: password.length >= minLength && hasUppercase && hasLowercase && hasNumbers && hasSpecialChars,
      errors: [
        password.length < minLength && `At least ${minLength} characters`,
        !hasUppercase && 'One uppercase letter',
        !hasLowercase && 'One lowercase letter',
        !hasNumbers && 'One number',
        !hasSpecialChars && 'One special character'
      ].filter(Boolean)
    };
  }

  /**
   * Verify admin credentials
   */
  verifyCredentials(username, password) {
    return username === this.adminCredentials.username && 
           password === this.adminCredentials.password;
  }

  /**
   * Get admin email
   */
  getAdminEmail() {
    return this.adminCredentials.email;
  }
}

// Export singleton instance
export default new AdminPasswordService();
